/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { TranslationCreationSteps, TranslationInfoDto } from 'src/translation/components/TranslationCreationPage';

type TranslationStepsNavigatorProps = {
    activeStep: number;
    isReturningUser: boolean;
    translationConfigsDto: TranslationInfoDto;
    onActiveStepChange: (newStep: number) => void;
};

const messages = defineMessages({
    basicInfo: { id: 'translation.translationStepsNavigator.basicInfo', defaultMessage: 'Basic information' },
    filesSource: { id: 'translation.translationStepsNavigator.filesSource', defaultMessage: 'Files selection' },
    reviewAndFinish: { id: 'translation.translationStepsNavigator.reviewAndFinish', defaultMessage: 'Review and finish' },
    filesDestination: { id: 'translation.translationStepsNavigator.filesDestination', defaultMessage: 'Files destination' },
    additionalOptions: { id: 'translation.translationStepsNavigator.additionalOptions', defaultMessage: 'Additional options' },
    initConfiguration: { id: 'translation.translationStepsNavigator.initConfiguration', defaultMessage: 'Initial Configuration' },
    filesAndDestination: { id: 'translation.translationStepsNavigator.filesAndDestination', defaultMessage: 'Files and destination' }
});

/**
 * Displays the side navigator in the document translation tool.
 */
export const TranslationStepsNavigator = (props: TranslationStepsNavigatorProps) => {
    const { activeStep, translationConfigsDto, onActiveStepChange, isReturningUser } = props;

    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();

    const isBasicInformationComplete = translationConfigsDto.sourceLanguage && translationConfigsDto.targetLanguages.length;
    const isFilesSelectionComplete = translationConfigsDto.selectedBlobFiles.length || translationConfigsDto.selectedLocalFiles.length;
    const isFilesDestinationComplete = translationConfigsDto.destinationContainerName || translationConfigsDto.isDownloadToLocalStorage;
    const isFilesAndDestinationComplete = isFilesSelectionComplete && isFilesDestinationComplete;
    const isStepsComplete = isBasicInformationComplete && isFilesAndDestinationComplete;

    const getStepStatus = (stepIndex: TranslationCreationSteps): 'none' | 'completed' | 'active' => {
        if (stepIndex === TranslationCreationSteps.FileSelection && activeStep === TranslationCreationSteps.FileDestination) {
            return 'active';
        }
        if (stepIndex === activeStep) {
            return 'active';
        }
        switch (stepIndex) {
            case TranslationCreationSteps.InitConfiguration:
                return 'active';
            case TranslationCreationSteps.BasicInformation:
                return isBasicInformationComplete ? 'completed' : 'none';
            case TranslationCreationSteps.FileSelection:
                return isFilesAndDestinationComplete ? 'completed' : 'none';
            case TranslationCreationSteps.FileDestination:
                return isFilesDestinationComplete ? 'completed' : 'none';
            case TranslationCreationSteps.ReviewAndFinish:
            case TranslationCreationSteps.AdditionalOptions:
                return isStepsComplete ? 'completed' : 'none';
            default:
                return 'completed';
        }
    };

    let steps: LuisStepperStepType[] = [];
    if (!isReturningUser) {
        steps = [
            ...steps,
            {
                id: TranslationCreationSteps[TranslationCreationSteps.InitConfiguration],
                title: localize(messages.initConfiguration),
                state: getStepStatus(TranslationCreationSteps.InitConfiguration)
            }
        ];
    }
    steps = [
        ...steps,
        {
            title: localize(messages.basicInfo),
            disabled: !isBasicInformationComplete,
            state: getStepStatus(TranslationCreationSteps.BasicInformation),
            id: TranslationCreationSteps[TranslationCreationSteps.BasicInformation]
        },
        {
            disabled: !isFilesSelectionComplete,
            title: localize(messages.filesAndDestination),
            state: getStepStatus(TranslationCreationSteps.FileSelection),
            id: TranslationCreationSteps[TranslationCreationSteps.FileSelection],
            children: isLargeOrGreater
                ? [
                      {
                          disabled: !isFilesSelectionComplete,
                          title: localize(messages.filesSource),
                          state: getStepStatus(TranslationCreationSteps.FileSelection),
                          id: TranslationCreationSteps[TranslationCreationSteps.FileSelection]
                      },
                      {
                          disabled: !isFilesDestinationComplete,
                          title: localize(messages.filesDestination),
                          state: getStepStatus(TranslationCreationSteps.FileDestination),
                          id: TranslationCreationSteps[TranslationCreationSteps.FileDestination]
                      }
                  ]
                : []
        },
        {
            disabled: !isStepsComplete,
            title: localize(messages.additionalOptions),
            state: getStepStatus(TranslationCreationSteps.AdditionalOptions),
            id: TranslationCreationSteps[TranslationCreationSteps.AdditionalOptions]
        },
        {
            disabled: !isStepsComplete,
            title: localize(messages.reviewAndFinish),
            state: getStepStatus(TranslationCreationSteps.ReviewAndFinish),
            id: TranslationCreationSteps[TranslationCreationSteps.ReviewAndFinish]
        }
    ];

    const onStepClick = (stepKey: string) => {
        onActiveStepChange(TranslationCreationSteps[stepKey as keyof typeof TranslationCreationSteps]);
    };

    return <LuisStepper onStepClick={onStepClick} horizontal={!isLargeOrGreater} steps={steps} />;
};
